/*
 * Shared status pill — a Mantine `Badge` driven by the re-homed status metadata
 * (`lib/status`) and the `StatusTone`→Mantine-color map (`lib/tone`). This is
 * the U6 successor to `design/StatusBadge`: feature components render run states
 * and audit verdicts through it so the whole console keeps one visual language.
 *
 * Drive it either by handing it a backend string + `kind` (it resolves tone +
 * label) or directly with an explicit `tone` + `label`.
 */

import { Badge } from "@mantine/core";
import {
  auditVerdictMeta,
  runStatusMeta,
  type StatusTone,
} from "../lib/status";
import { TONE_COLOR } from "../lib/tone";

export interface StatusPillProps {
  /** Pre-resolved tone, or let `kind` derive it from `value`. */
  tone?: StatusTone;
  /** Explicit label; defaults to the resolved label for `value`. */
  label?: string;
  /**
   * When provided alongside `value`, the pill derives tone + label from the
   * backend string. `"run"` maps the run states; `"verdict"` the audit verdicts.
   */
  kind?: "run" | "verdict";
  value?: string;
  /** Hide the leading status dot (e.g. in tight table cells). */
  hideDot?: boolean;
}

function resolve(props: StatusPillProps): { tone: StatusTone; label: string } {
  if (props.kind && props.value !== undefined) {
    const meta =
      props.kind === "run"
        ? runStatusMeta(props.value)
        : auditVerdictMeta(props.value);
    return { tone: props.tone ?? meta.tone, label: props.label ?? meta.label };
  }
  return {
    tone: props.tone ?? "neutral",
    label: props.label ?? props.value ?? "—",
  };
}

export function StatusPill(props: StatusPillProps) {
  const { tone, label } = resolve(props);
  return (
    <Badge
      color={TONE_COLOR[tone]}
      variant="light"
      radius="sm"
      size="sm"
      leftSection={
        props.hideDot ? undefined : (
          <span
            aria-hidden="true"
            style={{
              display: "inline-block",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: "currentColor",
            }}
          />
        )
      }
      styles={{ label: { textTransform: "none" } }}
    >
      {label}
    </Badge>
  );
}
